'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { GetCurationServiceResponse } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { curatorQueries } from '../query-factory';
import { EditSnapCurationForm } from './EditSnapCurationForm';
import { SelectSnapsSelectionStep } from './SelectSnapsSelectionStep';
import { useShopEditContext } from './ShopEditContext';
import { snapItemFromCurationDetail } from './snapCurationMappers';
import { type ShopSnapCurationEditFormData, shopSnapCurationEditFormSchema } from './type';
import { getCurationFormDraftKey, useCurationFormDraft } from './useCurationFormDraft';

interface Props {
  className?: string;
  curation: GetCurationServiceResponse;
}

export function ShopSnapCurationEdit({ className, curation }: Props) {
  const { step, setStep } = useShopEditContext();
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';

  const goToSelectSnaps = () => {
    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'snap_add_try',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: 'snap',
      });
    }
    setStep('step2');
  };

  const initialValues = useMemo<ShopSnapCurationEditFormData>(
    () => ({
      title: curation.title ?? '',
      description: curation.description ?? '',
      selectedSnaps: curation.snapItems.map(snapItemFromCurationDetail),
    }),
    [curation],
  );

  const form = useForm<ShopSnapCurationEditFormData>({
    resolver: zodResolver(shopSnapCurationEditFormSchema),
    defaultValues: initialValues,
  });
  useCurationFormDraft(
    curation.curationId ? getCurationFormDraftKey('snap', curation.curationId) : null,
    form,
    initialValues,
  );

  return (
    <FormProvider {...form}>
      <div className={twMerge('flex w-full grow flex-col', className)}>
        <EditSnapCurationForm
          className={step === 'step2' ? 'hidden' : ''}
          curation={curation}
          onClickSelectSnaps={goToSelectSnaps}
        />
        {step === 'step2' && (
          <SelectSnapsSelectionStep
            defaultCheckedSnaps={form.getValues('selectedSnaps')}
            onSubmit={() => setStep('step1')}
          />
        )}
      </div>
    </FormProvider>
  );
}
